import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Mail, Inbox, Building2, Ticket, ShieldCheck, Calendar, Loader2, Pencil, Check, X, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StudioProfileForm } from "@/components/StudioProfileForm";
import { COMPANY } from "@/lib/company";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-card border border-border rounded-md p-4 flex items-center gap-3" data-testid={`profile-stat-${label}`}>
    <div className="h-10 w-10 rounded-md bg-[#007BFF]/10 text-[#007BFF] flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-bold leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 truncate">{label}</div>
    </div>
  </div>
);

export default function ProfiloAccount() {
  const { user, setUser, logout } = useAuth();
  const [ws, setWs] = useState(null);
  const [exempt, setExempt] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const startEditName = () => { setNameDraft(user?.name || ""); setEditingName(true); };
  const saveName = async () => {
    const n = nameDraft.trim();
    if (!n) { toast.error("Il nome non può essere vuoto"); return; }
    setSavingName(true);
    try {
      const r = await api.put("/auth/me", { name: n });
      setUser((u) => ({ ...u, name: r.data?.name || n }));
      setEditingName(false);
      toast.success("Nome aggiornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aggiornamento non riuscito");
    } finally { setSavingName(false); }
  };

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/stats").catch(() => ({ data: {} })),
      api.get("/accounts/summary").catch(() => ({ data: { accounts: [] } })),
      api.get("/buildings").catch(() => ({ data: [] })),
      api.get("/tickets").catch(() => ({ data: [] })),
      api.get("/profile").catch(() => ({ data: {} })),
    ]).then(([s, a, b, t, p]) => {
      if (!alive) return;
      setExempt(!!p.data?.is_exempt);
      setWs({
        emails: s.data.total || 0,
        unread: s.data.unread || 0,
        accounts: (a.data.accounts || []).length,
        buildings: (b.data || []).length,
        tickets: (t.data || []).length,
      });
    });
    return () => { alive = false; };
  }, []);

  const created = user?.created_at ? new Date(user.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  return (
    <div className="min-h-full bg-[#F8F9FA]">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Il tuo account</div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tighter">Profilo</h1>
      </header>

      <div className="p-4 sm:p-10 space-y-6">
        {/* Identity card */}
        <div data-testid="profile-identity-card" className="bg-card border border-border rounded-lg p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {user?.picture ? (
            <img src={user.picture} alt="" data-testid="profile-avatar" className="h-20 w-20 rounded-full border border-border object-cover" />
          ) : (
            <div className="h-20 w-20 rounded-full bg-[#007BFF] text-white flex items-center justify-center text-3xl font-bold" data-testid="profile-avatar">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0 text-center sm:text-left">
            {editingName ? (
              <div className="flex items-center gap-2 justify-center sm:justify-start" data-testid="profile-name-edit">
                <Input
                  data-testid="profile-name-input"
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                  maxLength={80}
                  placeholder="Il tuo nome"
                  className="h-9 max-w-[240px] text-lg font-semibold"
                />
                <Button data-testid="profile-name-save" size="icon" onClick={saveName} disabled={savingName}
                  className="h-9 w-9 bg-[#007BFF] hover:bg-[#0069D9] shrink-0">
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button data-testid="profile-name-cancel" size="icon" variant="outline" onClick={() => setEditingName(false)} disabled={savingName}
                  className="h-9 w-9 shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <h2 data-testid="profile-name" className="font-head text-2xl font-bold tracking-tight">{user?.name || "Amministratore"}</h2>
                <button data-testid="profile-name-edit-button" onClick={startEditName}
                  aria-label="Modifica nome" title="Modifica nome"
                  className="text-muted-foreground hover:text-[#007BFF] transition-colors p-1 rounded">
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            )}
            <p data-testid="profile-email" className="text-muted-foreground flex items-center justify-center sm:justify-start gap-1.5 mt-1">
              <Mail className="h-4 w-4 shrink-0" />{user?.email}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center sm:justify-start gap-1.5 mt-2">
              <Calendar className="h-3.5 w-3.5 shrink-0" />Account attivo dal {created}
            </p>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
              <ShieldCheck className="h-3.5 w-3.5" />Connesso con Google
            </span>
            {exempt && (
              <span data-testid="profile-exempt-badge" title="Account gratuito: i condomìni risultano attivi senza pagamento."
                className="inline-flex items-center gap-1.5 mt-3 sm:ml-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                <Gift className="h-3.5 w-3.5" />Account esente (gratuito)
              </span>
            )}
          </div>
          <Button data-testid="profile-logout-button" onClick={logout} variant="outline"
            className="rounded-md border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0">
            <LogOut className="h-4 w-4 mr-2" />Esci
          </Button>
        </div>

        {/* Dati dello studio */}
        <div data-testid="studio-profile-card" className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-head text-lg font-semibold mb-1">Dati dello studio di amministrazione</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Questi dati identificano il tuo studio e sono usati in fatture, comunicazioni e portali. I campi con <span className="text-red-500">*</span> sono obbligatori.
          </p>
          <StudioProfileForm />
        </div>

        {/* Workspace overview */}
        <div>
          <h3 className="font-head text-lg font-semibold mb-1">La tua area di lavoro</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Questi dati sono privati e visibili <strong>solo a questo account</strong>. Accedendo con un altro account Google avrai un'area di lavoro separata.
          </p>
          {!ws ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin" />Carico i dati…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Mail} label="Caselle collegate" value={ws.accounts} />
              <StatCard icon={Inbox} label="Email totali" value={ws.emails} />
              <StatCard icon={Inbox} label="Non lette" value={ws.unread} />
              <StatCard icon={Building2} label="Condomìni" value={ws.buildings} />
              <StatCard icon={Ticket} label="Ticket" value={ws.tickets} />
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-5" data-testid="profile-legal-section">
          <div className="text-sm font-semibold mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#007BFF]" />Legale e AI Act</div>
          <p className="text-xs text-muted-foreground mb-3">Termini di utilizzo, privacy, cookie, condizioni del servizio {COMPANY.nome_prodotto} e informativa sull'Intelligenza Artificiale (AI Act).</p>
          <div className="flex flex-wrap gap-2">
            <a href="/legale?doc=termini" target="_blank" rel="noopener noreferrer" data-testid="profile-link-termini" className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-border hover:border-[#007BFF]/50 hover:text-[#007BFF] transition-colors">Termini e Condizioni</a>
            <a href="/legale?doc=privacy" target="_blank" rel="noopener noreferrer" data-testid="profile-link-privacy" className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-border hover:border-[#007BFF]/50 hover:text-[#007BFF] transition-colors">Informativa Privacy</a>
            <a href="/legale?doc=cookie" target="_blank" rel="noopener noreferrer" data-testid="profile-link-cookie" className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-border hover:border-[#007BFF]/50 hover:text-[#007BFF] transition-colors">Cookie Policy</a>
            <a href="/legale?doc=abbonamento" target="_blank" rel="noopener noreferrer" data-testid="profile-link-abbonamento" className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-border hover:border-[#007BFF]/50 hover:text-[#007BFF] transition-colors">Condizioni di Abbonamento</a>
            <a href="/legale?doc=ai" target="_blank" rel="noopener noreferrer" data-testid="profile-link-aiact" className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-border hover:border-[#007BFF]/50 hover:text-[#007BFF] transition-colors">Intelligenza Artificiale (AI Act)</a>
          </div>
        </div>
      </div>
    </div>
  );
}
